import React, { type FC, memo, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Calendar, cn, DateField, DatePicker, Modal, Spinner } from "@heroui/react";
import { type CalendarDate, getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { type GQL } from "@binale-tech/shared";
import { FormattedMessage, useIntl } from "react-intl";
import { CompanyContext } from "@domain/context/CompanyContext";
import { BanksContext } from "@domain/context/BanksContext";
import { type Step, StepProgressIndicator } from "./StepProgressIndicator";
import { FinApiWebFormEmbed } from "./FinApiWebFormEmbed";
import { FinApiResultAlert } from "./FinApiResultAlert";
import { FinApiAccountSelection } from "./FinApiAccountSelection";
import {
    useFinApiBankConnectionsLazy,
    useFinApiConnectionUpdate,
    useFinApiTransactionsImport,
} from "../hooks/useFinApiMutations";

type FinApiUpdateFormProps = {
    bankId: string;
    /** `modal` (default) wraps the form in its own modal; `inline` renders it in place, e.g. in the agent chat. */
    variant?: "modal" | "inline";
    isOpen?: boolean;
    onOpenChange?: () => void;
    /** Called once the import succeeded. */
    onFinished?: (result: GQL.IFinApiTransactionsImportResponse) => void;
    /** Called when a server write (connection update, transaction import) starts or ends. */
    onBusyChange?: (busy: boolean) => void;
};

const FinApiBankImportFormImpl: FC<FinApiUpdateFormProps> = ({
    bankId,
    variant = "modal",
    isOpen = true,
    onOpenChange,
    onFinished,
    onBusyChange,
}) => {
    const isInline = variant === "inline";
    const { companyGQL } = use(CompanyContext);
    const { banksById } = use(BanksContext);
    const { formatMessage, formatDate } = useIntl();
    const bank = banksById.get(bankId);

    const [step, setStep] = useState<number>(undefined);
    const [step1isLoading, setStep1isLoading] = useState(false);
    const [step1updateData, setStep1updateData] = useState<GQL.IFinApiConnectionUpdateResponse>();
    const [step2isLoading, setStep2isLoading] = useState(false);
    const [step2webForm, setStep2webForm] = useState<GQL.IFinApiWebFormResponse>();
    const [step2webFormComplete, setStep2webFormComplete] = useState(false);
    const [step3isLoading, setStep3isLoading] = useState(false);
    const [step3import, setStep3import] = useState<GQL.IFinApiTransactionsImportResponse>();

    const [connectionData, setConnectionData] = useState<GQL.IFinApiBankConnection>();
    const [showAccountSelection, setShowAccountSelection] = useState(false);

    const [apiError, setApiError] = useState<string>();

    // Transactions before the opening balance date are already in the opening balance.
    const openingBalanceDate = bank?.openingBalanceDate;
    const minDate = useMemo(
        () => (openingBalanceDate ? parseDate(openingBalanceDate) : today(getLocalTimeZone()).subtract({ years: 1 })),
        [openingBalanceDate],
    );
    const maxDate = useMemo(() => today(getLocalTimeZone()), []);
    const [importFromDate, setImportFromDate] = useState<CalendarDate>(() => {
        const finapiImportDate = banksById.get(bankId)?.finapiImportDate;
        return finapiImportDate
            ? parseDate(finapiImportDate).subtract({ days: 7 })
            : today(getLocalTimeZone()).subtract({ years: 1 });
    });

    const isImportFromDateValid =
        Boolean(importFromDate) && importFromDate.compare(minDate) >= 0 && importFromDate.compare(maxDate) <= 0;
    const formatCalendarDate = (date: CalendarDate) =>
        formatDate(date.toDate(getLocalTimeZone()), { day: "2-digit", month: "2-digit", year: "numeric" });

    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const isWriting = step1isLoading || step3isLoading;
    useEffect(() => onBusyChangeRef.current?.(isWriting), [isWriting]);

    const [fetchConnections] = useFinApiBankConnectionsLazy();
    const [connectionUpdate] = useFinApiConnectionUpdate();
    const [transactionsImport] = useFinApiTransactionsImport();

    useEffect(() => {
        if (!isOpen) {
            setStep(undefined);

            setStep1updateData(undefined);
            setStep1isLoading(false);
            setStep2webForm(undefined);
            setStep2webFormComplete(false);
            setStep2isLoading(false);
            setStep3import(undefined);
            setStep3isLoading(false);

            setConnectionData(undefined);
            setShowAccountSelection(false);

            setApiError(undefined);
        }
    }, [isOpen]);

    const handleStartUpdate = useCallback(async () => {
        setStep(0);
        setStep1isLoading(true);
        setApiError(undefined);

        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
            const result = await connectionUpdate({
                variables: {
                    input: {
                        companyId: companyGQL?.id,
                        id: bankId,
                    },
                },
            });

            setStep1updateData(result.data?.bankingBankFinAPIConnectionUpdate);

            setStep(1);
        } catch (error) {
            setApiError(error.message);
        } finally {
            setStep1isLoading(false);
        }
    }, [bankId, companyGQL?.id, connectionUpdate]);

    const handleImportTransactions = useCallback(async () => {
        setStep2isLoading(false);
        setStep3isLoading(true);
        try {
            const result = await transactionsImport({
                variables: {
                    input: {
                        companyId: companyGQL?.id,
                        bankId,
                        importFromDate: importFromDate.toString(),
                    },
                },
            });

            if (result.data?.bankingBankFinAPITransactionsImport?.error) {
                throw new Error(result.data?.bankingBankFinAPITransactionsImport?.error);
            }
            setStep3import(result.data?.bankingBankFinAPITransactionsImport);
            setStep(3);
            onFinishedRef.current?.(result.data.bankingBankFinAPITransactionsImport);
        } catch (error) {
            setApiError(error.message);
        } finally {
            setStep3isLoading(false);
        }
    }, [bankId, companyGQL?.id, importFromDate, transactionsImport]);

    const handleAccountSelected = useCallback(async () => {
        setShowAccountSelection(false);
        await handleImportTransactions();
    }, [handleImportTransactions]);

    const handleBankConnectionConfirmed = useCallback(async () => {
        setStep(2);
        setStep2webFormComplete(true);
        setStep2isLoading(false);

        if (bank?.finapiAccountId) {
            await handleImportTransactions();
            return;
        }

        try {
            const result = await fetchConnections({ variables: { companyId: companyGQL?.id } });
            const connections = result.data?.bankingBankConnections ?? [];
            const bankConnection = connections.find((c) => String(c.id) === String(bank?.finapiBankConnectionId));
            if (bankConnection) {
                setConnectionData(bankConnection);
                setShowAccountSelection(true);
            } else {
                setApiError("Bank connection not found. Please reconnect the bank.");
            }
        } catch (error) {
            setApiError(error.message);
        }
    }, [bank, companyGQL?.id, fetchConnections, handleImportTransactions]);

    useEffect(() => {
        if (!step1updateData) {
            return;
        }
        if (step1updateData.webForm) {
            setStep2isLoading(true);
            setStep2webForm(step1updateData.webForm);
        } else {
            handleBankConnectionConfirmed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step1updateData]);

    const steps: Step[] = useMemo(
        () => [
            {
                number: 1,
                label: formatMessage({ id: "app.banks.stepUpdateConnection" }),
                isComplete: step > 0,
                isLoading: step1isLoading,
            },
            {
                number: 2,
                label: formatMessage({ id: "app.confirmation.header" }),
                isComplete: step > 1,
                isLoading: step2isLoading,
                displayValue: step2webForm?.id,
            },
            {
                number: 3,
                label: formatMessage({ id: "app.banks.stepImportTransactions" }),
                isComplete: step > 2,
                isLoading: step3isLoading,
            },
        ],
        [formatMessage, step, step1isLoading, step2isLoading, step2webForm?.id, step3isLoading],
    );

    const isClosable = !step1isLoading && !step2isLoading && !step3isLoading;

    const body = (
        <div className="flex flex-col gap-8 items-center">
            {bank?.finapiImportDate && (
                <div className="text-grey-500 text-sm">
                    <FormattedMessage id="app.banks.lastImportDate" />: {bank.finapiImportDate}
                </div>
            )}
            <div className="flex gap-4 items-center">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">
                        <FormattedMessage id="app.banks.importFrom" />
                    </label>
                    <DatePicker
                        shouldForceLeadingZeros
                        value={importFromDate}
                        onChange={setImportFromDate}
                        minValue={minDate}
                        maxValue={maxDate}
                        isDisabled={Boolean(step1updateData)}
                        isInvalid={!isImportFromDateValid}
                        className="w-48"
                        aria-label={formatMessage({ id: "app.banks.importFrom" })}
                    >
                        <DateField.Group fullWidth>
                            <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
                            <DateField.Suffix>
                                <DatePicker.Trigger>
                                    <DatePicker.TriggerIndicator />
                                </DatePicker.Trigger>
                            </DateField.Suffix>
                        </DateField.Group>
                        <DatePicker.Popover>
                            <Calendar aria-label={formatMessage({ id: "app.banks.importFrom" })}>
                                <Calendar.Header>
                                    <Calendar.YearPickerTrigger>
                                        <Calendar.YearPickerTriggerHeading />
                                        <Calendar.YearPickerTriggerIndicator />
                                    </Calendar.YearPickerTrigger>
                                    <Calendar.NavButton slot="previous" />
                                    <Calendar.NavButton slot="next" />
                                </Calendar.Header>
                                <Calendar.Grid>
                                    <Calendar.GridHeader>
                                        {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                                    </Calendar.GridHeader>
                                    <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
                                </Calendar.Grid>
                            </Calendar>
                        </DatePicker.Popover>
                    </DatePicker>
                    {isImportFromDateValid ? (
                        <span className="text-xs text-muted">
                            <FormattedMessage id="app.banks.onlyNewTransactions" />
                        </span>
                    ) : (
                        <span className="text-xs text-danger" data-testid="import-from-date-error">
                            {formatMessage(
                                { id: "app.banks.importFromInvalid" },
                                { min: formatCalendarDate(minDate), max: formatCalendarDate(maxDate) },
                            )}
                        </span>
                    )}
                </div>
                <Button
                    isPending={step1isLoading}
                    onPress={handleStartUpdate}
                    size="lg"
                    className="w-80"
                    isDisabled={Boolean(step1updateData) || !isImportFromDateValid}
                >
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            <FormattedMessage id="app.button.update" />
                        </>
                    )}
                </Button>
            </div>

            <FinApiAccountSelection
                bankId={bankId}
                companyId={companyGQL?.id}
                connection={connectionData}
                isVisible={showAccountSelection}
                onConfirmed={handleAccountSelected}
                onError={setApiError}
            />

            <StepProgressIndicator steps={steps} show={Number.isInteger(step)} />

            <FinApiResultAlert result={step3import} error={apiError} />

            <FinApiWebFormEmbed
                webForm={step2webForm}
                isComplete={step2webFormComplete}
                onComplete={handleBankConnectionConfirmed}
                onError={setApiError}
            />
        </div>
    );

    if (isInline) {
        return (
            <div className="w-full" data-testid="finapi-import-inline">
                {body}
            </div>
        );
    }

    return (
        <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal.Container>
                <Modal.Dialog
                    className={cn(`w-250 max-w-250 transition-[width] duration-500 ease-in-out`, {
                        "w-[90vw] max-w-[90vw]": step2webForm && !step2webFormComplete,
                    })}
                >
                    {isClosable && <Modal.CloseTrigger />}
                    <Modal.Header>
                        <Modal.Heading>
                            <FormattedMessage id="app.banks.finapiImport" />
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>{body}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline" onPress={onOpenChange} isDisabled={!isClosable}>
                            <FormattedMessage id="app.button.close" />
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};

export const FinApiBankImportForm = memo(FinApiBankImportFormImpl);
